import { z } from 'zod';

import { fetchSettingsStaff } from './settings';
import type { StaffMember as SettingsStaffRecord } from './settings';
import { requestJson } from './requests';

export type StaffRole = 'OWNER' | 'MANAGER' | 'STAFF' | string;
export type StaffLifecycleStatus = 'active' | 'on_break' | 'off_duty' | 'invited' | 'inactive';

export type StaffMetricsRangeKey = 'today' | '7d' | '30d';

export interface StaffMetricsComparison {
  ordersWeek: number;
  revenueWeekCents: number;
  hoursWeek: number;
}

export interface StaffMetrics {
  rangeKey: StaffMetricsRangeKey;
  rangeLabel: string;
  rangeShortLabel: string;
  ordersToday: number;
  ordersWeek: number;
  revenueWeekCents: number;
  hoursToday: number;
  hoursWeek: number;
  attendanceRate: number;
  productivityScore: number;
  efficiencyScore: number;
  ordersPerHour: number;
  averageOrderValueCents: number;
  comparison: StaffMetricsComparison;
}

export interface StaffMetricsSummary {
  range: {
    key: StaffMetricsRangeKey;
    label: string;
    shortLabel: string;
    previousLabel: string;
    start: number;
    end: number;
    previousStart: number;
    previousEnd: number;
  };
  totals: {
    orders: number;
    revenueCents: number;
    hours: number;
  };
  comparison: {
    orders: number;
    revenueCents: number;
    hours: number;
  };
  deltas: {
    orders: number;
    revenueCents: number;
    hours: number;
  };
  deltaPercent: {
    orders: number | null;
    revenueCents: number | null;
    hours: number | null;
  };
}

export interface StaffMember {
  id: string;
  email: string;
  role: StaffRole;
  name?: string | null;
  displayName?: string | null;
  phone?: string | null;
  department?: string | null;
  defaultShift?: string | null;
  invitedAt?: string | null;
  joinedAt?: string | null;
  status?: StaffLifecycleStatus;
  currentStatus?: StaffLifecycleStatus;
  activeShiftId?: string | null;
  activeShiftTimezone?: string | null;
  lastClockEventAt?: string | null;
  pinRotatedAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
  restoredAt?: string | null;
  restoredBy?: string | null;
  metrics?: StaffMetrics;
}

const StaffMetricsComparisonSchema: z.ZodType<StaffMetricsComparison> = z.object({
  ordersWeek: z.number().nonnegative(),
  revenueWeekCents: z.number().nonnegative(),
  hoursWeek: z.number().min(0),
});

const StaffMetricsSchema: z.ZodType<StaffMetrics> = z.object({
  rangeKey: z.enum(['today', '7d', '30d']),
  rangeLabel: z.string(),
  rangeShortLabel: z.string(),
  ordersToday: z.number().nonnegative(),
  ordersWeek: z.number().nonnegative(),
  revenueWeekCents: z.number().nonnegative(),
  hoursToday: z.number().min(0),
  hoursWeek: z.number().min(0),
  attendanceRate: z.number().min(0).max(100),
  productivityScore: z.number().min(0),
  efficiencyScore: z.number().min(0),
  ordersPerHour: z.number().min(0),
  averageOrderValueCents: z.number().nonnegative(),
  comparison: StaffMetricsComparisonSchema,
});

const StaffMetricsSummarySchema: z.ZodType<StaffMetricsSummary> = z.object({
  range: z.object({
    key: z.enum(['today', '7d', '30d']),
    label: z.string(),
    shortLabel: z.string(),
    previousLabel: z.string(),
    start: z.number(),
    end: z.number(),
    previousStart: z.number(),
    previousEnd: z.number(),
  }),
  totals: z.object({
    orders: z.number(),
    revenueCents: z.number(),
    hours: z.number(),
  }),
  comparison: z.object({
    orders: z.number(),
    revenueCents: z.number(),
    hours: z.number(),
  }),
  deltas: z.object({
    orders: z.number(),
    revenueCents: z.number(),
    hours: z.number(),
  }),
  deltaPercent: z.object({
    orders: z.number().nullable(),
    revenueCents: z.number().nullable(),
    hours: z.number().nullable(),
  }),
});

export interface FetchStaffResponse {
  staff: StaffMember[];
  summary?: StaffMetricsSummary;
}

const StaffMemberSchema: z.ZodType<StaffMember> = z.object({
  id: z.string(),
  email: z.string(),
  role: z.string(),
  name: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  defaultShift: z.string().nullable().optional(),
  invitedAt: z.string().nullable().optional(),
  joinedAt: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  currentStatus: z.string().nullable().optional(),
  activeShiftId: z.string().nullable().optional(),
  activeShiftTimezone: z.string().nullable().optional(),
  lastClockEventAt: z.string().nullable().optional(),
  pinRotatedAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  deletedAt: z.string().nullable().optional(),
  deletedBy: z.string().nullable().optional(),
  restoredAt: z.string().nullable().optional(),
  restoredBy: z.string().nullable().optional(),
  metrics: StaffMetricsSchema.optional(),
});

const FetchStaffResponseSchema: z.ZodType<FetchStaffResponse> = z.object({
  staff: z.array(StaffMemberSchema),
  summary: StaffMetricsSummarySchema.optional(),
});

const InviteStaffResponseSchema: z.ZodType<InviteStaffResponse> = z.object({
  ok: z.boolean(),
  member: StaffMemberSchema.optional(),
  temporaryPin: z.string().optional(),
});

const StaffMemberEnvelopeSchema = z.object({
  member: StaffMemberSchema,
});

export interface FetchStaffOptions {
  signal?: AbortSignal;
  range?: StaffMetricsRangeKey;
}

export async function fetchStaff(options?: FetchStaffOptions): Promise<FetchStaffResponse> {
  const { signal, range } = options ?? {};
  const searchParams = new URLSearchParams();
  if (range) {
    searchParams.set('range', range);
  }
  const path = searchParams.size > 0 ? `/staff?${searchParams.toString()}` : '/staff';

  try {
    const [coreResponse, lifecycleResponse] = await Promise.all([
      requestJson<FetchStaffResponse>(path, { signal, schema: FetchStaffResponseSchema }),
      fetchSettingsStaff(signal).catch((error) => {
        console.warn('[fetchStaff] failed to load lifecycle data, continuing with core payload:', error);
        return null;
      }),
    ]);

    if (!lifecycleResponse) {
      return coreResponse;
    }

    const lifecycleMap = new Map<string, SettingsStaffRecord>(
      lifecycleResponse.staff.map((member) => [member.id, member]),
    );

    const mergedStaff = coreResponse.staff.map((member) => {
      const lifecycle = lifecycleMap.get(member.id);
      if (!lifecycle) {
        return member;
      }
      return {
        ...member,
        status: lifecycle.status ?? member.status,
        currentStatus: lifecycle.currentStatus ?? lifecycle.status ?? member.currentStatus,
        activeShiftId: lifecycle.activeShiftId ?? member.activeShiftId,
        activeShiftTimezone: lifecycle.activeShiftTimezone ?? member.activeShiftTimezone,
        lastClockEventAt: lifecycle.lastClockEventAt ?? member.lastClockEventAt,
        pinRotatedAt: lifecycle.pinRotatedAt ?? member.pinRotatedAt,
      };
    });

    return {
      ...coreResponse,
      staff: mergedStaff,
    };
  } catch (error) {
    console.error('[fetchStaff] API call failed, returning empty staff list:', error);
    // Return empty staff list when API fails to allow dashboard to load
    return { staff: [] };
  }
}

export interface InviteStaffPayload {
  email: string;
  role: StaffRole;
  name?: string;
  phone?: string;
  department?: string;
  defaultShift?: string;
  pin?: string;
}

export interface InviteStaffResponse {
  ok: boolean;
  member?: StaffMember;
  temporaryPin?: string;
}

export async function inviteStaff(payload: InviteStaffPayload): Promise<InviteStaffResponse> {
  return requestJson<InviteStaffResponse>('/staff/invite', {
    method: 'POST',
    body: payload,
    schema: InviteStaffResponseSchema,
  });
}

export interface UpdateStaffPayload {
  role?: StaffRole;
  name?: string | null;
  phone?: string | null;
  department?: string | null;
  defaultShift?: string | null;
}

export async function updateStaffRole(staffId: string, payload: UpdateStaffPayload): Promise<{ member: StaffMember }> {
  return requestJson<{ member: StaffMember }>(`/staff/${staffId}`, {
    method: 'PATCH',
    body: payload,
    schema: StaffMemberEnvelopeSchema,
  });
}
